import React, { useEffect, useState, useRef } from 'react'
import { LatLngExpression, Map, polygon } from 'leaflet'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import './work-user-maps-styles.scss'
import {
  LoadAccountMemberList, LoadFormList, LoadProjectList, LoadResourcePlaceList, LoadUserLocationList,
  LoadWorkFinishOptionList, LoadWorkLocationList
} from '@/javascript/domain/usecases'
import ErrorBox from '@/components/shared/boxes/ErrorBox'
import { MapBox, EmptyBox, Filters } from './components'

export type FetchLocationsParams = {
  date?: Date
  viewMode?: string[]
  searchText?: string
  resourcePlaces?: string[]
  forms?: string[]
  projects?: string[]
  providers?: string[]
  inspectors?: string[]
  status?: string
  finishOptions?: string[]
}

type Props = {
  loadAccountMemberList: LoadAccountMemberList
  loadFormList: LoadFormList
  loadProjectList: LoadProjectList
  loadResourcePlaces: LoadResourcePlaceList
  loadUserLocationList: LoadUserLocationList
  loadWorkFinishOptionList: LoadWorkFinishOptionList
  loadWorkLocationList: LoadWorkLocationList
}

const WorkUserMaps: React.FC<Props> = ({
  loadAccountMemberList, loadFormList, loadProjectList, loadResourcePlaces, loadUserLocationList,
  loadWorkFinishOptionList, loadWorkLocationList
}) => {
  const [locations, setLocations] = useState({
    works: [] as LoadWorkLocationList.Model[],
    users: [] as LoadUserLocationList.Model[],
    center: [0.0, 0.0] as LatLngExpression,
    bounds: null as L.LatLngBounds | null
  })
  const [isReady, setIsReady] = useState(false)
  const [isError, setIsError] = useState(false)
  const mapRef = useRef<Map>(null)
  useEffect(() => {
    fetchLocations()
  }, [])

  useEffect(() => {
    if (!isReady || locations.works.length === 0 && locations.users.length === 0) return
    function calculateCenterAndBounds() {
      const allLocations = [...locations.works, ...locations.users]
      if (allLocations.length === 0) return
      const polygonPoints =
        allLocations.map(location => [parseFloat(location.latitude), parseFloat(location.longitude)] as LatLngExpression)
      const polygonDraw = polygon(polygonPoints)
      const bounds = polygonDraw.getBounds()
      setLocations(old => ({ ...old, bounds: bounds, center: bounds.getCenter() }))
    }
    calculateCenterAndBounds()
  }, [locations.users, locations.works])

  useEffect(() => {
    if (mapRef.current && locations.bounds) {
      mapRef.current.fitBounds(locations.bounds)
    }
  }, [locations.bounds])

  const fetchLocations = async (params?: FetchLocationsParams) => {
    setIsReady(false)
    setIsError(false)
    const recentDate = dayjs(params?.date).isSame(dayjs(), 'date')
    const resolveUsers = (!params?.viewMode || params.viewMode.includes("users")) && recentDate
    const resolveWorks = !params?.viewMode || params.viewMode.includes("works")
    const [users, works] = await Promise.all([
      resolveUsers ? loadUserLocationList.load(params) : Promise.resolve([]),
      resolveWorks ? loadWorkLocationList.load(params) : Promise.resolve([])
    ]).catch(() => {
      setIsError(true)
      return [[], []]
    })
    setIsReady(true)
    setLocations({ ...locations, works, users })
  }
 
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', backgroundColor: '#FAFAFA' }}>
        <Container sx={{ padding: '48px 24px'}}>
          <Box sx={{ display: 'flex', paddingBottom: '16px', flexDirection: 'column', alignItems: 'flex-start', gap: '16px', alignSelf: 'stretch', marginBottom: '8px' }}>
            <Box>
              <Typography variant='h3'>Mapa</Typography>
              <Chip label='Beta' size='medium' sx={{ fontSize: '14px' }} />
            </Box>
          </Box>
          <Divider sx={{ margin: '24px 0' }} />
          <Filters
            fetchLocations={fetchLocations}
            fetchAccountMembers={loadAccountMemberList}
            fetchForms={loadFormList}
            fetchProjects={loadProjectList}
            fetchResourcePlaces={loadResourcePlaces}
            fetchWorkFinishOptions={loadWorkFinishOptionList}
            isReady={isReady}
          />
        </Container>
      </Box>
      <Box
        sx={{
          display: 'flex', padding: '48px 24px', flexDirection: 'column', gap: '16px', backgroundColor: '#FFFFFF',
          minHeight: '40vh'
        }} data-testid="content-box">
          {!isReady &&
            <Container
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}
              data-testid="loading-box">
              <CircularProgress />
            </Container>
          }
          {isError &&
          <Container sx={{ marginBottom: '16px' }}>
            <ErrorBox
              errorMessage="Ocorreu um problema ao buscar as informações do mapa. Clique novamente em 'Aplicar filtros' ou recarregue a página."
              onClose={() => setIsError(false)}
              title="Erro ao carregar os dados: Ocorreu um problema ao buscar as informações do mapa."
            />
          </Container>
          }
          {isReady &&
            (
              locations.works.length === 0 && locations.users.length === 0
                ? <EmptyBox />
                : <MapBox ref={mapRef} locations={locations} />
            )
          }
      </Box>
    </>
  )
}

export default WorkUserMaps
